import React from 'react';
import { Link } from 'react-router-dom';

import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import InsightsIcon from '@mui/icons-material/Insights';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import MovieIcon from '@mui/icons-material/Movie';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

interface ToolLink {
  name: string;
  description: string;
  path: string;
  icon: React.ReactNode;
}

interface ToolSection {
  name: string;
  description: string;
  accent: string;
  tint: string;
  icon: React.ReactNode;
  tools: ToolLink[];
}

const toolSections: ToolSection[] = [
  {
    name: 'Operations',
    description: 'Plan inventory, understand cash needs, and monitor fulfillment.',
    accent: '#2563EB',
    tint: '#EFF6FF',
    icon: <BusinessCenterOutlinedIcon />,
    tools: [
      {
        name: 'Inventory Forecast',
        description: 'Plan replenishment and review future inventory needs.',
        path: '/inventory',
        icon: <Inventory2Icon />,
      },
      {
        name: 'Cashflow',
        description: 'Review cash position and projected movement.',
        path: '/cashflow',
        icon: <AccountBalanceIcon />,
      },
      {
        name: 'ShipBob Performance',
        description: 'Monitor fulfillment and delivery performance.',
        path: '/shipbob-performance',
        icon: <LocalShippingIcon />,
      },
    ],
  },
  {
    name: 'Marketing Intelligence',
    description: 'Find opportunities and act on performance across paid channels.',
    accent: '#7C3AED',
    tint: '#F5F3FF',
    icon: <AnalyticsOutlinedIcon />,
    tools: [
      {
        name: 'Google Watchdog',
        description: 'Review Google Ads health and prioritized action items.',
        path: '/google-watchdog',
        icon: <FactCheckIcon />,
      },
      {
        name: 'Search Term Review',
        description: 'Review search terms and improve campaign targeting.',
        path: '/search-term-review',
        icon: <ManageSearchIcon />,
      },
      {
        name: 'Auction Intelligence',
        description: 'Understand auction competition and impression-share trends.',
        path: '/auction-intelligence',
        icon: <QueryStatsIcon />,
      },
      {
        name: 'Meta Ads Insights',
        description: 'Explore Meta advertising performance and trends.',
        path: '/meta-ads-insights',
        icon: <InsightsIcon />,
      },
      {
        name: 'SEO Decision Log',
        description:
          'What the SEO system did each day, why, and whether it worked.',
        path: '/seo',
        icon: <TravelExploreIcon />,
      },
    ],
  },
  {
    name: 'Creative',
    description: 'Research winning creative and turn source media into finished plans.',
    accent: '#DB2777',
    tint: '#FDF2F8',
    icon: <AutoAwesomeOutlinedIcon />,
    tools: [
      {
        name: 'Video Editing',
        description: 'Build timelines, trim clips, and prepare editor instructions.',
        path: '/video-editing',
        icon: <MovieIcon />,
      },
      {
        name: 'B-Roll Library',
        description: 'Search reusable footage, or add new clips from a Drive folder.',
        path: '/broll-library',
        icon: <VideoLibraryIcon />,
      },
      {
        name: 'Video Creative',
        description: 'Find and analyze competitor video ads, and build scripts from them.',
        path: '/video-creative',
        icon: <LibraryBooksIcon />,
      },
      {
        name: 'Static Creative',
        description: 'Find and analyze competitor image ads, and generate new ones with our product.',
        path: '/static-ads',
        icon: <ImageOutlinedIcon />,
      },
      {
        name: 'Creative Performance',
        description: 'See what past ads returned, by creative, headline and copy.',
        path: '/creative-performance',
        icon: <InsightsIcon />,
      },
    ],
  },
];

export default function HomePage() {
  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto', width: '100%', pb: 4 }}>
      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <Typography variant="overline" sx={{ color: 'primary.main' }}>
          SBLY Tools
        </Typography>
        <Typography variant="h3" sx={{ mt: 0.5, mb: 1 }}>
          What would you like to work on?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 680 }}>
          Jump directly into the tool you need. Everything is organized by the work it supports.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'repeat(3, minmax(0, 1fr))',
          },
          gap: 3,
          alignItems: 'start',
        }}
      >
        {toolSections.map(section => (
          <Paper
            component="section"
            key={section.name}
            sx={{
              p: { xs: 2.5, md: 3 },
              borderTop: `4px solid ${section.accent}`,
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 3 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: 2.5,
                  color: section.accent,
                  backgroundColor: section.tint,
                  '& .MuiSvgIcon-root': { fontSize: 24 },
                }}
              >
                {section.icon}
              </Box>
              <Box>
                <Typography variant="h5" sx={{ mb: 0.5 }}>
                  {section.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {section.description}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'grid', gap: 1.25 }}>
              {section.tools.map(tool => (
                <Box
                  component={Link}
                  to={tool.path}
                  key={tool.path}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.75,
                    color: 'text.primary',
                    textDecoration: 'none',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2.5,
                    backgroundColor: 'background.paper',
                    transition: 'border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease',
                    '&:hover': {
                      borderColor: section.accent,
                      boxShadow: '0 6px 16px rgba(15, 23, 42, 0.07)',
                      transform: 'translateY(-1px)',
                    },
                    '&:focus-visible': {
                      outline: `2px solid ${section.accent}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      flexShrink: 0,
                      display: 'grid',
                      placeItems: 'center',
                      borderRadius: 2,
                      color: section.accent,
                      backgroundColor: section.tint,
                      '& .MuiSvgIcon-root': { fontSize: 20 },
                    }}
                  >
                    {tool.icon}
                  </Box>
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography variant="subtitle1" sx={{ mb: 0.25 }}>
                      {tool.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {tool.description}
                    </Typography>
                  </Box>
                  <ArrowForwardRoundedIcon
                    sx={{ color: '#94A3B8', flexShrink: 0, fontSize: 20 }}
                  />
                </Box>
              ))}
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );
}
